import { type WorkerRequirementIncludedAutomatedNameUpdateType } from "@clipboard-health/contract-worker-app-bff";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { CbhIcon, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { type SetStateAction } from "react";

import { Button } from "../../components/Button";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";
import { OnboardingPageHeader, type OnboardingPageHeaderProps } from "../SharedPageHeader";
import { OnboardingTitle } from "../SharedTitle";
import { OnboardingPersonalIdVerificationApprovedBottomSheetContainer } from "./ApprovedBottomSheetContainer";
import { OnboardingPersonalIdVerificationInProgressBottomSheet } from "./InProgressBottomSheet";
import { OnboardingPersonalIdVerificationSelectSubtypeBottomSheet } from "./SelectSubtypeBottomSheet";
import {
  type OnboardingPersonalIdVerificationAnalytics,
  type OnboardingPersonalIdVerificationStep,
} from "./types";

interface OnboardingPersonalIdVerificationProps extends Pick<
  OnboardingPageHeaderProps,
  "progress"
> {
  requirementId: string;
  currentStep: OnboardingPersonalIdVerificationStep;
  setCurrentStep: (step: SetStateAction<OnboardingPersonalIdVerificationStep>) => void;
  onBack: () => void;
  onNext: () => void;
  analytics: OnboardingPersonalIdVerificationAnalytics;
  automatedNameUpdate?: WorkerRequirementIncludedAutomatedNameUpdateType;
}

export function OnboardingPersonalIdVerification({
  requirementId,
  currentStep,
  setCurrentStep,
  onBack,
  onNext,
  analytics,
  progress,
  automatedNameUpdate,
}: OnboardingPersonalIdVerificationProps) {
  const selectSubtypeModalState = useModalState();
  const approvedModalState = useModalState("open");
  const inProgressModalState = useModalState("open");

  useLogEffect(APP_V2_APP_EVENTS.ONBOARDING_PERSONAL_ID_VERIFICATION_PAGE_VIEWED, {
    ...analytics,
    currentStep,
  });

  return (
    <>
      <PageWrapper variant="tertiary">
        <OnboardingPageHeader progress={progress} onClickBack={onBack} />
        <PageContent>
          <Stack gap={4}>
            <OnboardingTitle text="Verify Your ID" />
            <Text variant="body1">
              To finish setting up your account, we&apos;ll confirm your identity with a quick,
              secure ID check powered by Stripe.
            </Text>
            <Text variant="body1">
              You&apos;ll take a clear photo of a government-issued ID and a quick selfie — it only
              takes a minute.
            </Text>
            <Text variant="body1">
              Once verified, you&apos;ll be one step closer to finding work opportunities on
              Clipboard!
            </Text>
          </Stack>
        </PageContent>
        <PageFooterWrapper>
          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            mb={8}
            color={(theme) => theme.palette.text.primary}
          >
            <CbhIcon type="triangle-exclamation" />
            <Text variant="body1" color="inherit">
              <strong>Tip:</strong> Make sure the name on your ID matches the name you entered
              earlier
            </Text>
          </Stack>
          <Button
            fullWidth
            variant="brand"
            size="large"
            onClick={() => {
              selectSubtypeModalState.openModal();
            }}
          >
            Start ID Verification
          </Button>
        </PageFooterWrapper>
      </PageWrapper>
      {currentStep === "start-verification" && (
        <OnboardingPersonalIdVerificationSelectSubtypeBottomSheet
          requirementId={requirementId}
          modalState={selectSubtypeModalState}
          analytics={analytics}
          onVerificationCompleted={() => {
            selectSubtypeModalState.closeModal();
            setCurrentStep("in-progress");
          }}
        />
      )}
      {currentStep === "in-progress" && (
        <OnboardingPersonalIdVerificationInProgressBottomSheet
          modalState={inProgressModalState}
          setCurrentStep={setCurrentStep}
        />
      )}
      {currentStep === "approved" && (
        <OnboardingPersonalIdVerificationApprovedBottomSheetContainer
          modalState={approvedModalState}
          isLoading={false}
          automatedNameUpdate={automatedNameUpdate}
          analytics={analytics}
          onContinue={onNext}
        />
      )}
    </>
  );
}
